import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react'
import { lumi } from '../lib/lumi'
import {
  applySiteFonts,
  buildGoogleFontsHref,
  DEFAULT_BODY_FONT,
  DEFAULT_FONT_WEIGHTS,
  DEFAULT_HEADING_FONT,
  FontWeights,
  normalizeFontWeights,
} from '../utils/fonts'

interface FontThemeContextValue {
  headingFont: string
  bodyFont: string
  weights: FontWeights
  loading: boolean
  saveFonts: (headingFont: string, bodyFont: string, weights: FontWeights) => Promise<void>
}

const FontThemeContext = createContext<FontThemeContextValue | null>(null)

const GOOGLE_FONTS_LINK_ID = 'site-dynamic-fonts'

function ensureGoogleFonts(headingId: string, bodyId: string) {
  const href = buildGoogleFontsHref(headingId, bodyId)
  const existing = document.getElementById(GOOGLE_FONTS_LINK_ID) as HTMLLinkElement | null
  if (!href) {
    existing?.remove()
    return
  }
  if (existing) {
    existing.href = href
    return
  }
  const link = document.createElement('link')
  link.id = GOOGLE_FONTS_LINK_ID
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

export function FontThemeProvider({ children }: { children: ReactNode }) {
  const [headingFont, setHeadingFont] = useState(DEFAULT_HEADING_FONT)
  const [bodyFont, setBodyFont] = useState(DEFAULT_BODY_FONT)
  const [weights, setWeights] = useState<FontWeights>(DEFAULT_FONT_WEIGHTS)
  const [recordId, setRecordId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const apply = useCallback(
    (nextHeading: string, nextBody: string, nextWeights?: Partial<FontWeights> | null) => {
      const resolved = normalizeFontWeights(nextWeights)
      setHeadingFont(nextHeading)
      setBodyFont(nextBody)
      setWeights(resolved)
      applySiteFonts(nextHeading, nextBody, resolved)
      ensureGoogleFonts(nextHeading, nextBody)
    },
    [],
  )

  useEffect(() => {
    apply(DEFAULT_HEADING_FONT, DEFAULT_BODY_FONT, DEFAULT_FONT_WEIGHTS)

    const loadFonts = async () => {
      try {
        const { list } = await lumi.entities.site_content.list({
          filter: { page: 'global', section: 'typography' },
          limit: 1,
        })
        const record = list?.[0]
        const nextHeading = record?.content?.headingFont || DEFAULT_HEADING_FONT
        const nextBody = record?.content?.bodyFont || DEFAULT_BODY_FONT
        if (record?._id) setRecordId(record._id)
        apply(nextHeading, nextBody, record?.content?.fontWeights)
      } catch (error) {
        console.error('Chargement des polices:', error)
      } finally {
        setLoading(false)
      }
    }

    loadFonts()
  }, [apply])

  const saveFonts = async (nextHeading: string, nextBody: string, nextWeights: FontWeights) => {
    apply(nextHeading, nextBody, nextWeights)
    const now = new Date().toISOString()
    const payload = {
      page: 'global',
      section: 'typography',
      content: {
        headingFont: nextHeading,
        bodyFont: nextBody,
        fontWeights: nextWeights,
      },
      updatedAt: now,
    }

    if (recordId) {
      await lumi.entities.site_content.update(recordId, payload)
      return
    }

    const created = await lumi.entities.site_content.create({
      ...payload,
      creator: lumi.auth.user?.userId || 'admin',
      creatorEmail: lumi.auth.user?.email || '',
      createdAt: now,
    })
    if (created?._id) setRecordId(created._id)
  }

  return (
    <FontThemeContext.Provider value={{ headingFont, bodyFont, weights, loading, saveFonts }}>
      {children}
    </FontThemeContext.Provider>
  )
}

export function useFontTheme() {
  const context = useContext(FontThemeContext)
  if (!context) {
    throw new Error('useFontTheme must be used within FontThemeProvider')
  }
  return context
}
